import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import slider1 from "../images/sliders/slider1.jpg";
import slider2 from "../images/sliders/slider2.jpg";
import slider3 from "../images/sliders/slider3.jpg";

const SLIDE_MS = 4000;

/*
  IMAGES: each slide has `image`.
*/
const slides = [
  {
    badge: "Elastic Tapes & Webbing",
    title: "High-Quality",
    highlight: "Elastic Tapes & Webbing",
    subtitle: "for Garments and Industry",
    description:
      "Woven and knitted elastic tapes, drawcords and stretch webbing, made to your width, color and stretch.",
    image: slider1,
    fallback: "from-slate-900 via-red-950 to-slate-900",
  },
  {
    badge: "Ribbons, Badges & Stickers",
    title: "Garment Trims That",
    highlight: "Finish Your Brand",
    subtitle: "Ribbons, Badges, Labels & Stickers",
    description:
      "Custom woven ribbons, garment badges and branded stickers produced to your design and quantity.",
    image: slider2,
    fallback: "from-slate-900 via-blue-950 to-slate-900",
  },
  {
    badge: "Direct Export Manufacturer",
    title: "Your Single-Source Partner for",
    highlight: "Elastic & Accessories",
    subtitle: "Supplying Buyers Worldwide",
    description:
      "In-house production with needle looms and printing, with export-ready packing and reliable delivery.",
    image: slider3,
    fallback: "from-slate-900 via-orange-950 to-slate-900",
  },
];

const HomeSlider = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [failed, setFailed] = useState({}); // tracks images that failed to load

  const next = useCallback(() => setActive((p) => (p + 1) % slides.length), []);
  const prev = useCallback(
    () => setActive((p) => (p - 1 + slides.length) % slides.length),
    []
  );

  useEffect(() => {
    if (paused) return;
    const id = setTimeout(next, SLIDE_MS);
    return () => clearTimeout(id);
  }, [active, paused, next]);

  const slide = slides[active];

  return (
    <section
      className="relative overflow-hidden bg-slate-900 text-white min-h-[600px] lg:min-h-[720px] flex items-center justify-center"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setTimeout(() => setPaused(false), 4000)}
    >
      <style>{`
        @keyframes heroFade { from {opacity:0; transform:translateY(16px)} to {opacity:1; transform:none} }
        @keyframes heroProgress { from {width:0} to {width:100%} }
        .hero-fade { animation: heroFade .7s ease-out both; }
        .hero-progress { animation: heroProgress ${SLIDE_MS}ms linear both; }
        .hero-paused .hero-progress { animation-play-state: paused; }
      `}</style>

      {/* BACKGROUND LAYER: gradient fallback + image on top */}
      {slides.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === active ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className={`absolute inset-0 bg-gradient-to-br ${s.fallback}`} />
          {!failed[i] && (
            <img
              src={s.image}
              alt=""
              loading={i === 0 ? "eager" : "lazy"}
              onError={() => setFailed((f) => ({ ...f, [i]: true }))}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          )}
        </div>
      ))}

      {/* Dark tint so white text is always readable (images still visible) */}
      <div className="absolute inset-0 bg-black/50 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40 pointer-events-none" />

      {/* FOREGROUND TEXT */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 sm:px-10 py-20 text-center">
        <div key={active} className="hero-fade flex flex-col items-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-wider bg-black/50 text-white border border-white/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#EB4438]" />
            {slide.badge}
          </span>

          <h1
            className="text-3xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight"
            style={{ textShadow: "0 2px 12px rgba(0,0,0,.7)" }}
          >
            <span className="block">{slide.title}</span>
            <span className="block text-[#FF6B5E]">{slide.highlight}</span>
            <span className="block text-xl sm:text-3xl lg:text-4xl font-semibold text-white/90 mt-3">
              {slide.subtitle}
            </span>
          </h1>

          <p
            className="mt-6 max-w-2xl text-base sm:text-lg text-white/90 leading-relaxed"
            style={{ textShadow: "0 1px 8px rgba(0,0,0,.8)" }}
          >
            {slide.description}
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/products"
              className="px-8 py-3.5 rounded-xl bg-[#EB4438] hover:bg-[#d9382c] text-white font-bold shadow-lg transition hover:-translate-y-0.5"
            >
              Explore Products
            </Link>
            <Link
              to="/contact"
              className="px-8 py-3.5 rounded-xl bg-white text-slate-900 hover:bg-slate-100 font-bold shadow-lg transition hover:-translate-y-0.5"
            >
              Request a Quote
            </Link>
          </div>
        </div>

        {/* Dots + progress */}
        <div className={`mt-12 flex items-center justify-center gap-3 ${paused ? "hero-paused" : ""}`}>
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`relative h-2.5 rounded-full overflow-hidden transition-all duration-500 ${
                active === i ? "w-12 bg-white/30" : "w-2.5 bg-white/50 hover:bg-white/80"
              }`}
            >
              {active === i && (
                <span key={active} className="hero-progress absolute inset-y-0 left-0 bg-[#EB4438]" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Arrows */}
      <button
        onClick={prev}
        aria-label="Previous slide"
        className="hidden sm:flex absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-black/40 hover:bg-black/70 border border-white/30 text-xl"
      >
        ‹
      </button>
      <button
        onClick={next}
        aria-label="Next slide"
        className="hidden sm:flex absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-black/40 hover:bg-black/70 border border-white/30 text-xl"
      >
        ›
      </button>
    </section>
  );
}

export default HomeSlider;